import { datadogRum } from '@datadog/browser-rum-slim';
import { CoreApplicationContext } from '@eventbrite/context-gen';
import { EBEnvs, getCurrentEnv } from '@eventbrite/feature-detection';
import { RenderGlobalNavOrUndefined } from '@eventbrite/global-nav';
import { configureStore } from '@eventbrite/redux-configure-store';
import { middleware as analyticsMiddleware } from '@eventbrite/site-analytics';
import React, { useEffect, useMemo } from 'react';
import { Provider } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import thunk from 'redux-thunk';
import { ConfirmationOverlay } from '../../components/ConfirmationOverlay';
import { ConsumerHeader } from '../../components/ConsumerHeader';
import { OverlayProvider } from '../../context/Overlay';
import { SearchServerEnrolledExperimentProvider } from '../../context/SearchServerEnrolledExperiment';
import reducer from '../../redux/reducers';
import { getInitialData } from '../../redux/reducers/utils';
import { FeatureFlags, FooterLinksShape } from '../../types';
import { ApiSearchData } from '../../types/Search';
import '../app.scss';
import { BasePage } from '../base';
import { SearchPageContent } from './SearchPageContent';
import { useInitialEventsExpansion } from './SearchPageDataExpansions';

type SearchPageProps = RouteComponentProps & {
    env: CoreApplicationContext['env'];
    app_version?: string;
    app_name: string;
    user: {
        isAuthenticated?: boolean;
        publicId?: string;
        email?: string;
        isStaff?: boolean;
    };
    request: { session_id: string };
    breadcrumbs?: Array<{ title: string; [key: string]: any }>;
    search_data: ApiSearchData;
    location: Location;
    featureFlags?: FeatureFlags;
    footerLinks: FooterLinksShape;
};

function SearchPage(props: SearchPageProps) {
    const {
        env,
        location,
        search_data,
        request,
        user,
        featureFlags,
        footerLinks,
    } = props;

    const GlobalNavHeader = RenderGlobalNavOrUndefined({
        config: {
            env,
            user,
            searchBar: { renderSearchBar: false },
        },
        featureFlag: featureFlags?.enableIaGlobalNav ?? false,
    });

    // TODO: Adjust dependencies of these memoizations
    // getInitialData requires a lot of props that are not currently being mapped
    // Sadly, using props as key is not valid, since it changes reference with every route change
    const initialState = useMemo(
        () => getInitialData(props),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );
    const store = useMemo(
        () =>
            configureStore({
                reducer,
                initialState,
                middleware: [thunk, analyticsMiddleware(props)],
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    const params = new URLSearchParams(location?.search || '');
    const basePageProps = { env, user, request, featureFlags, footerLinks };

    useDataDogEffects(env);
    useInitialEventsExpansion(search_data, initialState.search.eventSearch);

    return (
        <Provider store={store}>
            <SearchServerEnrolledExperimentProvider
                experimentEnrollments={search_data?.experiment_enrollment || {}}
                experimentEnrollmentOverrides={params}
            >
                <OverlayProvider>
                    <BasePage
                        {...basePageProps}
                        structureHeader={ConsumerHeader}
                        overrideHeader={GlobalNavHeader}
                        isSearchPage
                        removeOverflowSettings
                    >
                        <ConfirmationOverlay />
                        <div className="feed-search-page">
                            <SearchPageContent />
                        </div>
                    </BasePage>
                </OverlayProvider>
            </SearchServerEnrolledExperimentProvider>
        </Provider>
    );
}

function useDataDogEffects(env: CoreApplicationContext['env']) {
    const isProdEnv = getCurrentEnv({ env }) === EBEnvs.PROD;

    useEffect(() => {
        if (!isProdEnv) {
            return;
        }
        datadogRum.onReady(() =>
            datadogRum.addRumGlobalContext('view.sub_app', 'search'),
        );
    }, [isProdEnv]);
}

export default SearchPage;
